import type { CSSProperties } from "react";
import { Av, Journey, WinChip } from "@/components/bits";
import { ago, formatPhone } from "@/lib/format";
import { todo } from "@/model/advice";
import { isWaiting, type View } from "@/model/overview";
import type { Person } from "@/model/people";
import { batchOf, type RunRow } from "@/model/runs";
import { STATUS_LABEL } from "@/model/status";

export function batchLabel(p: Person, planned: number | null, rows: RunRow[]): string {
  if (p.status === "queued") return planned ? `Batch ${planned} · planned` : "Not in a batch";
  const t = p.primary?.ledger?.sentAt ?? p.primary?.ledger?.lastAttemptAt;
  return batchOf(t, rows)?.label ?? "Not in a batch";
}

/** One row (prototype pRow): compact in the split, extra columns when the list has the full width. */
export function PersonRow({
  p,
  on,
  view,
  now,
  planned,
  rows,
  style,
  onClick,
}: {
  p: Person;
  on: boolean;
  view: View;
  now: number;
  planned: number | null;
  rows: RunRow[];
  style?: CSSProperties;
  onClick: () => void;
}) {
  const waiting = isWaiting(p);
  const un = waiting ? (p.conv?.unread ?? 0) : 0;
  const t = todo(p, planned);
  // "Nothing · they have it" adds nothing to the status; other todos show after it (prototype t2)
  const short = t.text === "Nothing · they have it" ? "" : t.text.replace(/^Nothing · /, "");
  const showChat = !!p.conv?.lastInboundAt && (view === "replied" || waiting);
  const tm = showChat || p.status !== "queued" ? ago(p.lastAt, now) : "";
  return (
    <button
      type="button"
      className={`pr${on ? " on" : ""}${un ? " unread" : ""}`}
      data-p={p.id}
      data-status={p.status}
      style={style}
      onClick={onClick}
    >
      <Av name={p.name} />
      <span className="tx">
        <span className="n">
          <span className="nm">{p.name}</span>
          {p.regs.length > 1 ? <span className="fam">+{p.regs.length - 1}</span> : null}
          <span className="tm">{tm === "—" ? "" : tm}</span>
        </span>
        <span className="l2">
          <Journey s={p.status} />
          <span className="p">
            {showChat ? (
              p.conv?.lastPreview
            ) : (
              <>
                {STATUS_LABEL[p.status]}
                {short ? (
                  <span className="t2">
                    {" · "}
                    <span className={t.act ? "act" : ""}>{short}</span>
                  </span>
                ) : null}
              </>
            )}
          </span>
          {un ? <span className="ub">{un}</span> : null}
          {waiting ? <WinChip p={p} now={now} /> : null}
        </span>
      </span>
      <span className="wc mono w-ph">{p.phone ? formatPhone(p.phone) : "—"}</span>
      <span className="wc mono w-reg" title={p.regs.map((r) => r.key).join(", ")}>
        {p.regs[0]?.key ?? "—"}
        {p.regs.length > 1 ? ` +${p.regs.length - 1}` : ""}
      </span>
      <span className="wc w-b">{batchLabel(p, planned, rows)}</span>
      <span className={`wc w-td${t.act ? " act" : ""}`}>{t.text}</span>
      <span className="wc w-tm num">{tm || "—"}</span>
    </button>
  );
}
